import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Theme selection.
 *
 * Three states, not two. "System" is the default and is a real choice, not an absence of
 * one: a studio that works in a bright room in the morning and a dark one at night wants the
 * app to follow the machine, and collapsing that into a plain on/off switch means picking a
 * side and being wrong half the day.
 *
 * The applied class lives on `<html>`, matching `darkMode: 'class'` in the Tailwind config.
 * It is written by the inline script in `index.html` *before first paint* — see the note
 * there — so this provider's job is to keep it in step afterwards, not to establish it.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'theme';

interface ThemeContextValue {
  /** What the user asked for. */
  preference: ThemePreference;
  /** What that resolves to right now, with "system" answered. */
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function prefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    // Storage can be blocked outright in a locked-down browser profile. Not a reason to
    // refuse to render.
    return 'system';
  }
}

function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  /*
   * Keeps the browser chrome — address bar on mobile, and the surround on some desktop
   * builds — from staying light while the page goes dark.
   */
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', resolved === 'dark' ? '#0f1115' : '#f3f4f6');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [systemDark, setSystemDark] = useState(prefersDark);

  // The machine can change theme while the app is open — a scheduled switch at sunset, or
  // the user flipping it in system settings. Following it is the whole point of "system".
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const listen = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', listen);
    return () => query.removeEventListener('change', listen);
  }, []);

  const resolved: ResolvedTheme =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Preference will not survive a reload; the session still honours it.
    }
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Same trade-off as `useAuth`: keeping the hook beside its provider is worth the file
 * exporting a non-component, which limits fast refresh to a full reload for this file only.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
}
